import { Stack } from '@mui/material'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { useEffect, useMemo, useRef, useState } from 'react'
import MapIcon from 'assets/icons/nucleo/18px_map-position.svg'
import { EventCard } from 'components/EventCard'
import { Button, ButtonVariant } from 'components/common/Button'
import { Loader } from 'components/common/Loader'
import {
  defaultSortBy,
  searchClient,
  searchIndex
} from 'containers/meilisearchClient'
import defaultStartDate from 'domain/constants/EditionLiveStartDateTimestamp'
import { isNotNullOrUndefined } from 'helpers/array'
import { AutocompleteItem } from 'interfaces/algolia'
import {
  SeeMoreButtonContainer,
  StyledMapDisplayButtonContainer
} from './EventsInfiniteScroll.style'

interface BuildFilterParams {
  editionLiveLevel2Category?: string[] | string
  fromTimestamp?: string | string[]
  toTimestamp?: string | string[]
  geoSearch?: string | string[]
}

const buildFiltersString = ({
  editionLiveLevel2Category,
  fromTimestamp,
  toTimestamp,
  geoSearch
}: BuildFilterParams): string => {
  const categories = [editionLiveLevel2Category]
    .flat()
    .filter(isNotNullOrUndefined)

  const [north, east, south, west] =
    typeof geoSearch === 'string' ? geoSearch.split(',').map(Number) : []

  return [
    typeof geoSearch === 'string'
      ? `_geoBoundingBox([${north}, ${east}], [${south}, ${west}])`
      : null,
    `editionLiveStartDateTimestamp>=${fromTimestamp ?? defaultStartDate}`,
    fromTimestamp && toTimestamp
      ? `editionLiveEndDateTimestamp<=${toTimestamp}`
      : null,
    'eventStatus="LIVE"',
    categories.length > 0
      ? categories
          .map((cat) => `editionLiveLevel2CategoryKey=${cat}`)
          .join(' OR ')
      : null
  ]
    .filter(isNotNullOrUndefined)
    .join(' AND ')
}

const attributesToRetrieve = [
  'eventName',
  'eventCity',
  'eventLiveDistanceKm',
  'eventCountrySubdivisionDisplayCodeLevel2',
  'editionLiveLevel1CategoryKey',
  'eventSlug',
  'editionCalendarStatus',
  'eventLivePriceStartingFrom',
  'editionLiveStartDateTimestamp',
  'editionLiveEndDateTimestamp',
  'eventCoverImage',
  'editionLiveLevel2CategoryKey',
  'eventIsFeatured',
  'objectID'
]

interface EventsInfiniteScrollProps {
  closeDrawer: () => void
}

export const EventsInfiniteScroll = ({
  closeDrawer
}: EventsInfiniteScrollProps) => {
  const { query } = useRouter()

  const { t } = useTranslation(['common', 'home'])
  const [events, setEvents] = useState<AutocompleteItem[]>([])
  const [page, setPage] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [hasMore, setHasMore] = useState<boolean>(true)
  const eventsRef = useRef<HTMLDivElement>(null)
  const [hideFixedButton, setHideFixedButton] = useState(false)

  const {
    editionLiveLevel2Category = [],
    q,
    fromTimestamp,
    toTimestamp,
    geoSearch,
    sortBy
  } = query

  const params = useMemo(() => {
    return {
      query: (q as string) ?? '',
      hitsPerPage: 20,
      attributesToRetrieve,
      attributesToHighlight: ['objectID'],
      filters: buildFiltersString({
        editionLiveLevel2Category,
        fromTimestamp,
        toTimestamp,
        geoSearch
      })
    }
  }, [query])

  const fetchEvents = async (nextPage = page + 1) => {
    const response = await searchClient.search<AutocompleteItem>([
      {
        indexName:
          sortBy === '' ? searchIndex : (sortBy as string) || defaultSortBy,
        params: { ...params, page: nextPage }
      }
    ])

    setIsLoading(false)
    if ('hits' in response.results[0]!) {
      setEvents([
        ...(nextPage === 0 ? [] : events),
        ...response.results[0].hits
      ])
      setHasMore(response.results[0].nbPages > nextPage + 1)
    }
  }

  const loadNextEvents = async () => {
    setIsLoading(true)
    await fetchEvents()
    setPage((prevPage) => prevPage + 1)
  }

  useEffect(() => {
    fetchEvents(0).catch((error) => console.error(error))
  }, [params])

  useEffect(() => {
    const handleScroll = () => {
      const el = eventsRef.current
      if (!el) return

      const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight

      setHideFixedButton(distanceToBottom < 150)
    }

    const scrollEl = eventsRef.current
    if (scrollEl) {
      scrollEl.addEventListener('scroll', handleScroll)
    }

    return () => {
      if (scrollEl) {
        scrollEl.removeEventListener('scroll', handleScroll)
      }
    }
  }, [])

  return (
    <Stack
      p={2}
      spacing={2}
      ref={eventsRef}
      position="relative"
      maxHeight="100%"
      overflow="auto"
    >
      {events.map((event) => (
        <EventCard key={event.objectID} hit={event} isEventSelected={false} />
      ))}
      {events.length === 0 && isLoading && <Loader />}
      <StyledMapDisplayButtonContainer
        $hideFixedButton={hasMore && hideFixedButton}
      >
        <Button
          label={t('map.show')}
          variant={ButtonVariant.Secondary}
          leftIcon={<MapIcon />}
          onClick={closeDrawer}
        />
      </StyledMapDisplayButtonContainer>
      {hasMore && (
        <SeeMoreButtonContainer>
          <Button
            label={t('home:filters.seeMore')}
            icon={isLoading ? <Loader /> : undefined}
            disabled={isLoading}
            variant={ButtonVariant.Quaternary}
            onClick={loadNextEvents}
          />
        </SeeMoreButtonContainer>
      )}
    </Stack>
  )
}
